export const SUPPORTED_LOCALES = ["ko", "en"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "ko";
export const LOCALE_STORAGE_KEY = "locktree.locale";
export const LOCALE_QUERY_PARAMETER = "lang";

type LocaleListener = (locale: Locale) => void;
type LocaleStorageReader = Pick<Storage, "getItem">;
type LocaleStorageWriter = Pick<Storage, "setItem">;

let currentLocale: Locale = DEFAULT_LOCALE;
let translationLocaleReader: () => Locale = getLocale;
const localeListeners = new Set<LocaleListener>();

export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

export function getLocale(): Locale {
  return currentLocale;
}

/**
 * 컴포넌트 밖 번역이 쓸 로케일. 화면 어댑터가 등록한 읽기를 거치므로 코어와 worker 번들은
 * 프레임워크를 모른 채, computed 안의 번역은 언어 전환을 따라간다.
 */
export function readTranslationLocale(): Locale {
  return translationLocaleReader();
}

export function registerTranslationLocaleReader(reader: () => Locale): void {
  translationLocaleReader = reader;
}

/**
 * 사용자의 명시적 언어 전환. 저장 → html[lang] → 구독자 통지 순서이며 같은 값이면 아무것도 하지 않는다.
 */
export function setLocale(nextLocale: Locale): void {
  if (nextLocale === currentLocale) return;
  persistLocale(nextLocale, safeLocalStorage(globalThis));
  applyLocale(nextLocale);
}

export function subscribeToLocale(listener: LocaleListener): () => void {
  localeListeners.add(listener);
  return () => {
    localeListeners.delete(listener);
  };
}

/**
 * 우선순위: `?lang=` → 저장된 선택 → 기본 로케일. 지원하지 않는 값은 다음 후보로 넘어간다.
 */
export function resolveInitialLocale(
  search: string,
  storage: LocaleStorageReader | null
): Locale {
  const queryLocale = new URLSearchParams(search).get(LOCALE_QUERY_PARAMETER);
  if (isSupportedLocale(queryLocale)) return queryLocale;
  const storedLocale = readStoredLocale(storage);
  if (storedLocale) return storedLocale;
  return DEFAULT_LOCALE;
}

/**
 * 앱 마운트 전에 한 번 호출한다. `?lang=` 은 이번 로드에만 적용하고 저장하지 않는다 —
 * 공유 링크의 언어가 받는 사람의 저장된 선택을 덮어쓰지 않게 하기 위해서다.
 */
export function initializeLocale(
  window: Pick<Window, "location" | "localStorage">
): Locale {
  const resolvedLocale = resolveInitialLocale(
    window.location.search,
    safeLocalStorage(window)
  );
  applyLocale(resolvedLocale);
  return resolvedLocale;
}

export function resetLocaleForTests(): void {
  applyLocale(DEFAULT_LOCALE);
}

function applyLocale(nextLocale: Locale): void {
  const isChanged = nextLocale !== currentLocale;
  currentLocale = nextLocale;
  applyDocumentLanguage(nextLocale);
  if (!isChanged) return;
  for (const listener of localeListeners) {
    listener(nextLocale);
  }
}

function readStoredLocale(storage: LocaleStorageReader | null): Locale | null {
  if (!storage) return null;
  try {
    const storedValue = storage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(storedValue) ? storedValue : null;
  } catch {
    return null;
  }
}

function persistLocale(
  locale: Locale,
  storage: LocaleStorageWriter | null
): void {
  if (!storage) return;
  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // 저장이 막힌 환경(프라이빗 모드·용량 초과)에서는 이번 세션 동안만 선택이 유지된다.
  }
}

function safeLocalStorage(scope: { localStorage?: Storage }): Storage | null {
  try {
    return scope.localStorage ?? null;
  } catch {
    return null;
  }
}

function applyDocumentLanguage(locale: Locale): void {
  if (typeof document === "undefined") return;
  document.documentElement.lang = locale;
}
